import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, MessageCircle, Upload, History, FileText, Users, Bell, Settings, Target, Play, Pause } from 'lucide-react';
import * as XLSX from 'xlsx';

const WhatsAppNotificationsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [activeTab, setActiveTab] = useState('send');
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Estado para envío individual
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [countryCode, setCountryCode] = useState('52');
  
  // Estado para plantillas
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateVars, setTemplateVars] = useState({});
  
  // Estado para envío masivo
  const [excelData, setExcelData] = useState([]);
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkProgress, setBulkProgress] = useState(null);
  
  // Estado para configuración de notificaciones automáticas
  const [notifConfig, setNotifConfig] = useState({
    enabled: true,
    new_client: true,
    new_payment: true,
    new_router: true,
    expense_reminder: true
  });
  
  // Estado para campañas
  const [usersWithoutRouters, setUsersWithoutRouters] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [campaignDelay, setCampaignDelay] = useState(5);
  const [campaignProgress, setCampaignProgress] = useState(null);
  const [campaignRunning, setCampaignRunning] = useState(false);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
    }
    fetchTemplates();
    fetchLogs();
    fetchNotifConfig();
  }, [user, navigate]);
  
  const fetchUsersWithoutRouters = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/users-without-routers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setUsersWithoutRouters(data.users || []);
      setSelectedUsers(data.users?.map(u => u.user_id) || []);
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendCampaign = async () => {
    if (selectedUsers.length === 0) {
      alert('⚠️ Selecciona al menos un usuario');
      return;
    }
    
    const confirmMsg = `¿Enviar campaña a ${selectedUsers.length} usuarios con delay de ${campaignDelay} segundos?\n\nTiempo estimado: ${Math.ceil(selectedUsers.length * campaignDelay / 60)} minutos`;
    
    if (!window.confirm(confirmMsg)) return;
    
    setCampaignRunning(true);
    setCampaignProgress({ sent: 0, failed: 0, total: selectedUsers.length });
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/campaign/no-router`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_ids: selectedUsers,
          delay_seconds: campaignDelay
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert(`✅ Campaña completada!\n\nEnviados: ${data.statistics.sent}\nFallidos: ${data.statistics.failed}`);
        setCampaignProgress(data.statistics);
        fetchUsersWithoutRouters();
      } else {
        alert(`❌ Error: ${data.detail}`);
      }
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    } finally {
      setCampaignRunning(false);
    }
  };
  
  const fetchNotifConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/auto-notifications-config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setNotifConfig(data.config || notifConfig);
    } catch (error) {
      console.error('Error cargando configuración:', error);
    }
  };
  
  const handleSaveNotifConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/auto-notifications-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(notifConfig)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert('✅ Configuración guardada exitosamente');
      } else {
        alert(`❌ Error: ${data.detail || 'Error guardando configuración'}`);
      }
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
    }
    fetchTemplates();
    fetchLogs();
  }, [user, navigate]);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/whatsapp/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error cargando plantillas:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/whatsapp/logs?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Error cargando logs:', error);
    }
  };

  const handleSendIndividual = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/whatsapp/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ phone, message, country_code: countryCode })
      });
      
      // SIEMPRE consumir el body
      const data = await response.json().catch(() => ({}));
      
      if (response.ok) {
        alert('✅ Mensaje enviado exitosamente');
        setPhone('');
        setMessage('');
        fetchLogs();
      } else {
        alert(`❌ Error: ${data.detail || 'No se pudo enviar el mensaje'}`);
      }
    } catch (error) {
      console.error('Error enviando mensaje:', error);
      alert(`❌ Error de red: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSendTemplate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/whatsapp/send-template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          template_name: selectedTemplate,
          recipient_phone: phone,
          variables: templateVars,
          country_code: countryCode
        })
      });
      
      // SIEMPRE consumir el body
      const data = await response.json().catch(() => ({}));
      
      if (response.ok) {
        alert('✅ Mensaje con plantilla enviado exitosamente');
        setPhone('');
        setTemplateVars({});
        fetchLogs();
      } else {
        alert(`❌ Error: ${data.detail || 'No se pudo enviar el mensaje'}`);
      }
    } catch (error) {
      console.error('Error enviando template:', error);
      alert(`❌ Error de red: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(sheet);
        
        setExcelData(data);
        alert(`✅ Archivo cargado: ${data.length} registros encontrados`);
      } catch (error) {
        alert(`❌ Error leyendo archivo: ${error.message}`);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleBulkSend = async () => {
    if (excelData.length === 0) {
      alert('⚠️  Primero carga un archivo Excel');
      return;
    }
    
    if (!bulkMessage.trim()) {
      alert('⚠️  Escribe un mensaje para enviar');
      return;
    }
    
    if (!window.confirm(`¿Enviar mensaje a ${excelData.length} destinatarios?`)) {
      return;
    }
    
    setLoading(true);
    setBulkProgress({ sent: 0, total: excelData.length });
    
    try {
      const token = localStorage.getItem('token');
      
      // Mapear datos del Excel
      const recipients = excelData.map(row => ({
        phone: row.telefono || row.phone || row.Telefono || row.Phone || '',
        name: row.nombre || row.name || row.Nombre || row.Name || 'Cliente',
        country_code: row.pais || row.country || '52'
      }));
      
      const response = await fetch(`${API_URL}/api/notifications/whatsapp/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recipients: recipients,
          message_template: bulkMessage
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert(`✅ Envío completado: ${data.statistics.sent} enviados, ${data.statistics.failed} fallidos`);
        setBulkProgress(data.statistics);
        setExcelData([]);
        setBulkMessage('');
        fetchLogs();
      } else {
        alert(`❌ Error: ${data.detail || 'Error en envío masivo'}`);
      }
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/whatsapp/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // SIEMPRE consumir el body para evitar conflictos con interceptores
      const data = await response.json().catch(() => ({}));
      
      if (response.ok) {
        alert('✅ Prueba exitosa! Revisa tu WhatsApp');
      } else {
        alert(`❌ Error: ${data.detail || 'Error en prueba'}`);
      }
    } catch (error) {
      console.error('Error en prueba de conexión:', error);
      alert(`❌ Error de red: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderTemplateVariables = () => {
    const template = templates.find(t => t.name === selectedTemplate);
    if (!template) return null;

    const variables = template.variables.map(v => v.replace(/[{}]/g, ''));

    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-600">Variables de la plantilla:</p>
        {variables.map((varName, index) => (
          <div key={index}>
            <label className="block text-sm font-medium mb-1">{varName}</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={templateVars[varName] || ''}
              onChange={(e) => setTemplateVars({...templateVars, [varName]: e.target.value})}
              placeholder={`Ingresa ${varName}`}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/super-admin')}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Notificaciones WhatsApp</h1>
                <p className="text-sm text-gray-600">Gestiona envíos de mensajes WhatsApp</p>
              </div>
            </div>
            <button
              onClick={handleTestConnection}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <Bell className="w-4 h-4" />
              <span>Probar Conexión</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('send')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'send'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Send className="w-4 h-4" />
                  <span>Envío Individual</span>
                </div>
              </button>
              
              <button
                onClick={() => setActiveTab('template')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'template'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>Plantillas</span>
                </div>
              </button>
              
              <button
                onClick={() => setActiveTab('bulk')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'bulk'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4" />
                  <span>Envío Masivo</span>
                </div>
              </button>
              
              <button
                onClick={() => setActiveTab('logs')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'logs'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <History className="w-4 h-4" />
                  <span>Historial</span>
                </div>
              </button>
              
              <button
                onClick={() => setActiveTab('config')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'config'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Bell className="w-4 h-4" />
                  <span>Configuración</span>
                </div>
              </button>
              
              <button
                onClick={() => {
                  setActiveTab('campaigns');
                  fetchUsersWithoutRouters();
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'campaigns'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Target className="w-4 h-4" />
                  <span>Campañas</span>
                </div>
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Envío Individual */}
            {activeTab === 'send' && (
              <div className="max-w-2xl">
                <h3 className="text-lg font-semibold mb-4">Enviar Mensaje Individual</h3>
                <form onSubmit={handleSendIndividual} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Código de País</label>
                    <select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="52">🇲🇽 México (+52)</option>
                      <option value="1">🇺🇸 USA (+1)</option>
                      <option value="34">🇪🇸 España (+34)</option>
                      <option value="54">🇦🇷 Argentina (+54)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Número de Teléfono</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="5551234567"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Mensaje</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      rows="5"
                      placeholder="Escribe tu mensaje aquí..."
                      required
                    />
                    <p className="text-sm text-gray-500 mt-1">{message.length} caracteres</p>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-2"
                  >
                    <Send className="w-5 h-5" />
                    <span>{loading ? 'Enviando...' : 'Enviar Mensaje'}</span>
                  </button>
                </form>
              </div>
            )}

            {/* Plantillas */}
            {activeTab === 'template' && (
              <div className="max-w-2xl">
                <h3 className="text-lg font-semibold mb-4">Enviar con Plantilla</h3>
                <form onSubmit={handleSendTemplate} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Seleccionar Plantilla</label>
                    <select
                      value={selectedTemplate}
                      onChange={(e) => {
                        setSelectedTemplate(e.target.value);
                        setTemplateVars({});
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      required
                    >
                      <option value="">-- Selecciona una plantilla --</option>
                      {templates.map((template) => (
                        <option key={template.name} value={template.name}>
                          {template.name.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {selectedTemplate && (
                    <>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm font-medium mb-2">Vista previa:</p>
                        <p className="text-sm text-gray-700 whitespace-pre-line">
                          {templates.find(t => t.name === selectedTemplate)?.content}
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Código de País</label>
                        <select
                          value={countryCode}
                          onChange={(e) => setCountryCode(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="52">🇲🇽 México (+52)</option>
                          <option value="1">🇺🇸 USA (+1)</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Número de Teléfono</label>
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="5551234567"
                          required
                        />
                      </div>
                      
                      {renderTemplateVariables()}
                      
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        {loading ? 'Enviando...' : 'Enviar con Plantilla'}
                      </button>
                    </>
                  )}
                </form>
              </div>
            )}

            {/* Envío Masivo */}
            {activeTab === 'bulk' && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Envío Masivo desde Excel</h3>
                
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">📋 Formato del Excel:</h4>
                  <p className="text-sm text-blue-800">
                    Tu archivo debe contener las columnas: <strong>telefono</strong>, <strong>nombre</strong> (opcional), <strong>pais</strong> (opcional, default: 52)
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">1. Cargar Archivo Excel</label>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleExcelUpload}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    {excelData.length > 0 && (
                      <p className="text-sm text-green-600 mt-2">
                        ✅ {excelData.length} registros cargados
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">2. Mensaje para Todos</label>
                    <textarea
                      value={bulkMessage}
                      onChange={(e) => setBulkMessage(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      rows="5"
                      placeholder="Hola {{nombre}}, este es un mensaje masivo..."
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Puedes usar variables: {'{'}{'{'}}nombre{'}'}{'}'}, {'{'}{'{'}}telefono{'}'}{'}'}, etc.
                    </p>
                  </div>
                  
                  {bulkProgress && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-medium mb-2">Resultado del último envío:</p>
                      <div className="text-sm space-y-1">
                        <p>Total: {bulkProgress.total}</p>
                        <p className="text-green-600">Enviados: {bulkProgress.sent}</p>
                        <p className="text-red-600">Fallidos: {bulkProgress.failed}</p>
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={handleBulkSend}
                    disabled={loading || excelData.length === 0}
                    className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {loading ? 'Enviando...' : `Enviar a ${excelData.length} destinatarios`}
                  </button>
                </div>
              </div>
            )}

            {/* Historial */}
            {activeTab === 'logs' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Historial de Envíos</h3>
                  <button
                    onClick={fetchLogs}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Actualizar
                  </button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destinatario</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mensaje</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {logs.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                            No hay mensajes enviados aún
                          </td>
                        </tr>
                      ) : (
                        logs.map((log, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(log.sent_at).toLocaleString('es-MX')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {log.to_number}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-md truncate">
                              {log.message}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                log.status === 'sent' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {log.status === 'sent' ? '✓ Enviado' : '✗ Fallido'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Configuración de Notificaciones Automáticas */}
            {activeTab === 'config' && (
              <div className="max-w-3xl">
                <h3 className="text-lg font-semibold mb-4">Notificaciones Automáticas</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Configura qué eventos te notifican por WhatsApp automáticamente
                </p>
                
                <div className="space-y-6">
                  {/* Sección de Clientes */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="font-medium text-gray-900 mb-4">📱 Notificaciones de Clientes</h4>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">🆕 Nuevo cliente registrado</p>
                          <p className="text-sm text-gray-600">Te avisa cuando alguien se registra en el sistema</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notifConfig.new_client}
                            onChange={(e) => setNotifConfig({...notifConfig, new_client: e.target.checked})}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">🖥️ Nuevo router configurado</p>
                          <p className="text-sm text-gray-600">Te avisa cuando un cliente agrega un router</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notifConfig.new_router}
                            onChange={(e) => setNotifConfig({...notifConfig, new_router: e.target.checked})}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Sección de Pagos */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="font-medium text-gray-900 mb-4">💰 Notificaciones de Pagos</h4>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">💰 Nuevo pago por aprobar</p>
                          <p className="text-sm text-gray-600">Te avisa cuando un cliente sube comprobante de pago</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notifConfig.new_payment}
                            onChange={(e) => setNotifConfig({...notifConfig, new_payment: e.target.checked})}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">⏰ Recordatorios de gastos</p>
                          <p className="text-sm text-gray-600">Recordatorios de hosting, servicios, etc.</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notifConfig.expense_reminder}
                            onChange={(e) => setNotifConfig({...notifConfig, expense_reminder: e.target.checked})}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Botón Guardar */}
                  <button
                    onClick={handleSaveNotifConfig}
                    disabled={loading}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Guardando...' : 'Guardar Configuración'}
                  </button>
                  
                  {/* Info adicional */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>📱 Número de notificaciones:</strong> +52 {user?.phone || '953 106 4743'}
                    </p>
                    <p className="text-sm text-blue-800 mt-2">
                      Las notificaciones se envían cuando están activadas. Puedes desactivarlas temporalmente sin perder tu configuración.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Tab de Campañas */}
            {activeTab === 'campaigns' && (
              <div>
                <h3 className="text-lg font-semibold mb-4">📊 Campaña: Usuarios sin Router</h3>
                
                <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-900 mb-2">⚠️ Reglas de WhatsApp</h4>
                  <ul className="text-sm text-yellow-800 space-y-1">
                    <li>• Delay mínimo: 3 segundos (evita ban)</li>
                    <li>• No enviar al mismo usuario más de 1 vez por semana</li>
                    <li>• Solo usuarios registrados hace 3+ días</li>
                  </ul>
                </div>
                
                <div className="space-y-6">
                  {/* Estadísticas */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-600">Total encontrados</p>
                      <p className="text-2xl font-bold text-blue-900">{usersWithoutRouters.length}</p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm text-green-600">Seleccionados</p>
                      <p className="text-2xl font-bold text-green-900">{selectedUsers.length}</p>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <p className="text-sm text-purple-600">Tiempo estimado</p>
                      <p className="text-2xl font-bold text-purple-900">
                        {Math.ceil(selectedUsers.length * campaignDelay / 60)} min
                      </p>
                    </div>
                  </div>
                  
                  {/* Configuración de delay */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <label className="block text-sm font-medium mb-3">
                      ⏱️ Delay entre mensajes: <strong>{campaignDelay} segundos</strong>
                    </label>
                    <input
                      type="range"
                      min="3"
                      max="30"
                      value={campaignDelay}
                      onChange={(e) => setCampaignDelay(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      disabled={campaignRunning}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>3 seg (rápido)</span>
                      <span>15 seg (recomendado)</span>
                      <span>30 seg (ultra seguro)</span>
                    </div>
                  </div>
                  
                  {/* Lista de usuarios */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium">Usuarios sin router</h4>
                      <button
                        onClick={fetchUsersWithoutRouters}
                        className="text-sm text-blue-600 hover:text-blue-800"
                        disabled={loading}
                      >
                        Actualizar
                      </button>
                    </div>
                    
                    {usersWithoutRouters.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">
                        {loading ? 'Cargando...' : '✅ No hay usuarios pendientes de contactar'}
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {usersWithoutRouters.map((user, idx) => (
                          <div key={user.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                            <div className="flex-1">
                              <p className="font-medium">{user.full_name}</p>
                              <p className="text-sm text-gray-600">{user.email}</p>
                              <p className="text-xs text-gray-500">
                                Registrado hace {user.days_since_registration} días • {user.phone}
                              </p>
                            </div>
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.user_id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedUsers([...selectedUsers, user.user_id]);
                                } else {
                                  setSelectedUsers(selectedUsers.filter(id => id !== user.user_id));
                                }
                              }}
                              className="w-5 h-5"
                              disabled={campaignRunning}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Progreso */}
                  {campaignProgress && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="font-medium mb-3">Progreso de campaña:</p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Total: {campaignProgress.total}</span>
                          <span className="text-green-600">Enviados: {campaignProgress.sent}</span>
                          <span className="text-red-600">Fallidos: {campaignProgress.failed}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{width: `${(campaignProgress.sent + campaignProgress.failed) / campaignProgress.total * 100}%`}}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Botones */}
                  <div className="flex space-x-4">
                    <button
                      onClick={() => setSelectedUsers(usersWithoutRouters.map(u => u.user_id))}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      disabled={campaignRunning}
                    >
                      Seleccionar Todos
                    </button>
                    <button
                      onClick={() => setSelectedUsers([])}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      disabled={campaignRunning}
                    >
                      Deseleccionar Todos
                    </button>
                  </div>
                  
                  <button
                    onClick={handleSendCampaign}
                    disabled={campaignRunning || selectedUsers.length === 0}
                    className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center space-x-2"
                  >
                    {campaignRunning ? (
                      <>
                        <Play className="w-5 h-5 animate-pulse" />
                        <span>Enviando campaña...</span>
                      </>
                    ) : (
                      <>
                        <Target className="w-5 h-5" />
                        <span>Enviar Campaña a {selectedUsers.length} usuarios</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppNotificationsPage;
